import { PlotDataEx } from "components/dashboard/charts/interfaces";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import Plotly, { LayoutAxis } from "plotly.js";
import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { T } from "services/lang/LanguageService";
import { BAR_CHART_DATA_POINTS_LIMIT, dweenColorway } from "utils/constants";
import { getChartSizeValue } from "utils/utils";
import { XAxisType } from "./bar-chart-utils";

const commonAxisConfig = {
  showspikes: true,
  spikemode: "toaxis",
  automargin: true,
} as Partial<LayoutAxis>;

const layoutConfig = {
  legend: {
    orientation: "h",
  },
  margin: {
    l: 0,
    r: 0,
    b: 0,
    t: 20,
  },
} as unknown as Plotly.Layout;

type DatasetBarChartProps = {
  data: PlotDataEx[];
  width?: number;
  height: number | string;
  dateFormat?: string;
  chartConfig?: any;
  xaxisType?: XAxisType;
  skipWarning?: boolean;
  hideLegend?: boolean;
  xAxisTitle?: string;
  yTickConfig?: { tickvals: number[]; ticktext: string[] };
  isStacked?: boolean;
  hideBarLabels?: boolean;
};

const DatasetBarChart = ({
  data,
  width,
  height,
  dateFormat,
  chartConfig,
  xaxisType = XAxisType.DATE,
  skipWarning,
  hideLegend,
  xAxisTitle,
  yTickConfig,
  isStacked = false,
  hideBarLabels = false,
}: DatasetBarChartProps) => {
  const [confirmation, setConfirmation] = useState(false);

  useEffect(() => {
    setConfirmation(false);
    const dataPointsCount = data.reduce((sum, d) => sum + d.x.length, 0);
    if (dataPointsCount > BAR_CHART_DATA_POINTS_LIMIT && !skipWarning) {
      easasConfirmDialog({
        header: T("confirm-dialog.render-confirmation"),
        message: T(
          "confirm-dialog.message.are-you-sure-you-want-to-proceed-render-bar-chart-with-data-points",
          {
            dataPointsCount,
          }
        ),
        icon: "pi pi-exclamation-triangle",
        confirmedCallback: () => setConfirmation(true),
      });
    } else {
      setConfirmation(true);
    }
  }, [data]);

  if (data.length === 0) {
    return <h2 className="flex justify-content-center">{T("word.no-data")}</h2>;
  }

  const unit = data[0].unit || "";

  const getCategoryOrder = () => {
    if (xaxisType === "category") return "category ascending";
    if (xaxisType === "array") return "array";
    return "trace";
  };

  const getXAxisType = () => {
    if (xaxisType === "category" || xaxisType === "array") return "category";
    return "date";
  };

  return (
    <Plot
      data={
        confirmation
          ? data.map(trace => ({
              ...trace,
              ...(hideBarLabels
                ? {
                    text: undefined,
                    textposition: undefined,
                  }
                : {}),
            }))
          : []
      }
      className="ea-plotly-bar-chart"
      style={{ width: width || "100%" }}
      config={chartConfig}
      layout={{
        ...layoutConfig,
        height: getChartSizeValue(height),
        xaxis: {
          showticklabels: true,
          tickformat: dateFormat,
          type: getXAxisType(),
          title: { text: xAxisTitle, standoff: 5 },
          categoryorder: getCategoryOrder(),
          ...commonAxisConfig,
        },
        yaxis: {
          showticklabels: true,
          title: { text: unit, standoff: 10 },
          tickformat: "f",
          ...(yTickConfig ? { tickvals: yTickConfig.tickvals, ticktext: yTickConfig.ticktext } : {}),
          ...commonAxisConfig,
        },
        showlegend: !hideLegend,
        barmode: isStacked ? "stack" : "group",
        bargroupgap: 0.1,
        colorway: dweenColorway,
      }}
    />
  );
};

export default DatasetBarChart;
